import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { dispatch } from "../../../state/store";
import { setSelectedConsentElement } from "../../../state/ducks";

import {
  alignItems,
  consentAttributes,
  consentFlagOptions,
  consentGroups,
  consentTypes,
  strCode
} from "../../../consts";
import { consentUtil, dropdownUtil, objectUtil } from "../../../utils";
import { historyService, log, notificationService, t } from "../../../services";

import {
  DropdownInput,
  FlexContainers,
  Input,
  Paragraph2,
  Switcher,
  WrapperContainer
} from "../../../components";
import { FormSection } from "../../../templates";
import ConsentTextInput from "./components/ConsentTextInput/ConsentTextInput";
import {
  ButtonsWrapper,
  ConsentFormWrapper,
  ConsentFromLeftSectionWrapper,
  ConsentFromWrapper,
  ContentDropdownsWrapper,
  FormButton
} from "./styled";

const consentFormIds = {
  name: "name",
  groupName: "groupName",
  position: "position",
  type: "type",
  isActive: "isActive",
  code: "code",
  consentFlags: "consentFlags",
  texts: "texts",
  isVisible: "isVisible"
};

const formatConsentFlagOptions = (options) => {
  return options?.map((opt) => {
    return { id: opt.id, value: `${opt.value} - ${t(opt?.description)}` };
  });
};

const NewConsentForm = ({
  consentsData,
  consentPosition,
  onSaveConsent,
  isViewMode,
  viewOnlyLangs = [],
  isActive,
  isToggleVisibilityDisabled
}) => {
  const [name, setName] = useState();
  const [groupName, setGroupName] = useState();
  const [position, setPosition] = useState(0);
  const [type, setType] = useState();

  const [isVisible, setIsVisible] = useState(true);

  const [code, setCode] = useState();
  const [consentFlags, setConsentFlags] = useState([]);

  const [texts, setTexts] = useState([]);
  const [invalidIds, setInvalidIds] = useState([]);

  const [isSameData, setIsSameData] = useState(false);

  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);

  const history = historyService.getHistory();

  const formattedConsentFlagOptions = useMemo(() => {
    return formatConsentFlagOptions(consentFlagOptions);
  }, [consentFlagOptions]);

  const getConsentData = useCallback(() => {
    const newData = {
      name,
      groupName: groupName?.id,
      position: position && parseInt(position),
      type: type?.id,
      isConsentVisible: isVisible,

      code: code?.id,
      consentFlags: consentFlags.map((flag) => flag.id)?.sort(),

      texts: texts.map((text) => {
        return { languageCode: text.title, content: text.text, title: text.consentTitle };
      })
    };

    if (!isVisible) {
      delete newData.groupName;
      delete newData.type;
      delete newData.code;
      delete newData.texts;
    }

    return newData;
  }, [name, groupName, position, type, code, consentFlags, texts, isVisible]);

  const getOriginalConsentData = useCallback(() => {
    return {
      name: consentsData.name,
      groupName: consentsData.groupName,
      position: consentsData?.position && parseInt(consentsData.position),
      type: consentsData.type,
      isConsentVisible: consentsData?.isConsentVisible,

      code: consentsData.code,
      consentFlags: consentsData?.consentFlags?.sort(),

      texts: consentsData.texts
    };
  }, [consentsData]);

  useEffect(() => {
    if (hasBeenSubmitted) {
      const consent = getConsentData();
      validateData(consent);
    }
  }, [isVisible]);

  useEffect(() => {
    prepopulateData();
  }, [consentsData]);

  const prepopulateData = useCallback(() => {
    if (consentsData) {
      setName(consentsData?.name);
      setGroupName(
        consentsData?.groupName &&
          consentGroups.find((option) => option.id === consentsData.groupName)
      );
      setPosition(consentsData?.position);
      setType(consentsData?.type && consentTypes.find((option) => option.id === consentsData.type));

      setCode(
        consentsData?.code && consentAttributes.find((option) => option.id === consentsData.code)
      );
      setConsentFlags(
        consentsData?.consentFlags && consentsData.consentFlags?.length > 0
          ? consentFlagOptions.filter((option) => consentsData.consentFlags.includes(option.id))
          : []
      );

      setTexts(
        consentsData?.texts && consentsData?.texts?.length
          ? consentsData.texts.map((text, index) => {
              return {
                id: index,
                consentTitle: text?.title,
                title: text?.languageCode,
                text: text?.content
              };
            })
          : []
      );

      if ("isConsentVisible" in consentsData) {
        setIsVisible(consentsData.isConsentVisible);
      }

      if (
        !consentsData?.groupName ||
        !consentsData?.type ||
        !consentsData?.code ||
        !consentsData?.texts
      ) {
        setIsVisible(false);
      }
    } else {
      // align default texts with existing consent elements for the new one
      const defaultLangs = consentUtil.getDefaultConsentElementLangs();

      if (defaultLangs && defaultLangs?.length) {
        setTexts(
          defaultLangs.map((l, index) => {
            return { id: index, consentTitle: l.title, title: l.languageCode, text: "" };
          })
        );
      }
    }
  }, [consentsData]);

  useEffect(() => {
    if (consentPosition) {
      setPosition(consentPosition);
    }
  }, [consentPosition]);

  useEffect(() => {
    if (consentsData) {
      const originalData = getOriginalConsentData();
      const newData = getConsentData();

      originalData.texts = consentUtil.formatConsentTexts(originalData?.texts);
      newData.texts = consentUtil.formatConsentTexts(newData?.texts);

      setIsSameData(objectUtil.objEquals(originalData, newData));
    }
  }, [name, groupName, position, type, code, consentFlags, texts, consentsData, isVisible]);

  const validateData = (consent) => {
    const newInvalidIds = [];

    if (!consent.name) {
      newInvalidIds.push(consentFormIds.name);
    }

    if (isVisible && !consent.groupName) {
      newInvalidIds.push(consentFormIds.groupName);
    }

    if (isVisible && !consent.type) {
      newInvalidIds.push(consentFormIds.type);
    }

    if (isVisible && !consent.code) {
      newInvalidIds.push(consentFormIds.code);
    }

    let hasEmptyText = false;

    texts?.forEach((text) => {
      if (
        text?.text
          ?.replaceAll("<p>", "")
          ?.replaceAll("</p>", "")
          ?.replaceAll(/&nbsp;/g, "")
          ?.replaceAll(/\n/g, "")?.length === 0
      ) {
        hasEmptyText = true;
      }
    });

    if (isVisible && hasEmptyText) {
      newInvalidIds.push(consentFormIds.texts);
    }

    setInvalidIds(newInvalidIds);

    return newInvalidIds;
  };

  const resetFormData = () => {
    // setName(null);
    // setGroupName(null);
    // setType(null);
    // setIsVisible(true);

    // setCode(null);
    // setConsentFlags([]);

    // setTexts([]);

    dispatch(setSelectedConsentElement(null));
    setHasBeenSubmitted(false);
  };

  const onClose = () => {
    history.goBack();
    resetFormData();
  };

  const onSubmit = async (event) => {
    event.stopPropagation();
    const consent = getConsentData();
    setHasBeenSubmitted(true);

    const validationResult = validateData(consent);

    if (validationResult?.length === 0) {
      await onSaveConsent(consent, isVisible);

      history.goBack();
      resetFormData();
    } else {
      log.warn("NewConsentForm: Unable to update form due to missing fields");
      notificationService.showError({
        title: t(strCode.MISSING_FIELDS),
        message: t(strCode.MISSING_FIELDS_MESSAGE)
      });
    }
  };

  return (
    <WrapperContainer
      Container={FlexContainers.FlexColumnStart}
      alignItems={alignItems.start}
      width={"100%"}
      height={"100%"}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnBetween}
        width={"100%"}
        height={"100%"}
        mt={24}>
        <>
          <WrapperContainer width={"100%"}>
            <ConsentFromWrapper
              Container={FlexContainers.FlexRowStart}
              alignItems={alignItems.start}
              width={"100%"}
              gap={16}>
              <ConsentFromLeftSectionWrapper
                Container={FlexContainers.FlexColumnStart}
                minWidth={"20%"}
                gap={16}>
                <FormSection title={t(strCode.EXPLICIT_ATTRIBUTES_SECTION_TITLE)}>
                  <WrapperContainer
                    Container={FlexContainers.FlexColumnStart}
                    width={"100%"}
                    gap={16}
                    mb={8}>
                    <Input
                      id={consentFormIds.name}
                      title={t(strCode.NAME_TITLE)}
                      value={name}
                      setValue={setName}
                      placeholder={t(strCode.NAME_TITLE)}
                      hasError={invalidIds.includes(consentFormIds.name)}
                      viewOnly={isViewMode}
                    />

                    <DropdownInput
                      id={consentFormIds.groupName}
                      title={t(strCode.GROUP_NAME_TITLE)}
                      placeholder={t(strCode.SELECT_PLACEHOLDER)}
                      options={consentGroups}
                      selectedOption={groupName}
                      onSelectOption={(id) =>
                        setGroupName(consentGroups.find((opt) => opt.id === id))
                      }
                      hasError={invalidIds.includes(consentFormIds.groupName)}
                      viewOnly={isViewMode || !isVisible}
                    />

                    <Input
                      id={consentFormIds.position}
                      title={t(strCode.POSITION_TITLE)}
                      value={position}
                      placeholder={t(strCode.POSITION_PLACEHOLDER)}
                      type={"number"}
                      hasError={invalidIds.includes(consentFormIds.position)}
                      viewOnly
                    />

                    <DropdownInput
                      id={consentFormIds.type}
                      title={t(strCode.TYPE_TITLE)}
                      placeholder={t(strCode.SELECT_PLACEHOLDER)}
                      options={consentTypes}
                      selectedOption={type}
                      onSelectOption={(id) => setType(consentTypes.find((opt) => opt.id === id))}
                      hasError={invalidIds.includes(consentFormIds.type)}
                      viewOnly={isViewMode || !isVisible}
                    />
                  </WrapperContainer>
                </FormSection>
              </ConsentFromLeftSectionWrapper>

              <FormSection title={t(strCode.CONTENT_SECTION_TITLE)}>
                <ConsentFormWrapper isViewOnly={isViewMode}>
                  <ContentDropdownsWrapper
                    Container={FlexContainers.FlexRow}
                    alignItems={alignItems.start}
                    width="100%"
                    gap={16}
                    mb={32}>
                    <WrapperContainer minWidth={220} maxWidth={220}>
                      <Switcher
                        id={consentFormIds.isVisible}
                        title={t(strCode.CONSENT_VISIBLE_TITLE)}
                        checked={isVisible}
                        onSwitch={setIsVisible}
                        disabled={isViewMode || isToggleVisibilityDisabled}
                      />
                    </WrapperContainer>

                    <DropdownInput
                      id={consentFormIds.code}
                      title={t(strCode.ATTRIBUTES_TITLE)}
                      placeholder={t(strCode.SELECT_PLACEHOLDER)}
                      options={consentAttributes}
                      selectedOption={code}
                      onSelectOption={(id) =>
                        setCode(consentAttributes.find((opt) => opt.id === id))
                      }
                      hasError={invalidIds.includes(consentFormIds.code)}
                      viewOnly={isViewMode || !isVisible}
                    />
                    <DropdownInput
                      id={consentFormIds.consentFlags}
                      title={t(strCode.CONSENT_FLAGS_TITLE)}
                      placeholder={t(strCode.SELECT_PLACEHOLDER)}
                      options={formattedConsentFlagOptions}
                      selectedOptions={consentFlags}
                      onSelectOption={(id) => {
                        setConsentFlags(
                          dropdownUtil.updateSelectedOptions(
                            id,
                            consentFlags,
                            consentFlagOptions,
                            true
                          )
                        );
                      }}
                      showCheckboxes
                      hasError={invalidIds.includes(consentFormIds.consentFlags)}
                      viewOnly={isViewMode}
                    />
                  </ContentDropdownsWrapper>

                  <Paragraph2>{t(strCode.TEXT_SECTION_TITLE)}</Paragraph2>

                  <ConsentTextInput
                    languageList={texts}
                    setLanguageList={(langs) => setTexts(langs)}
                    hasError={invalidIds.includes(consentFormIds.texts)}
                    viewOnlyLangs={
                      isViewMode || !isVisible
                        ? !isVisible
                          ? texts.map((t) => t.title)
                          : viewOnlyLangs
                        : []
                    }
                    isEditDisabled={(isViewMode && !isActive) || !isVisible}
                  />
                </ConsentFormWrapper>
              </FormSection>
            </ConsentFromWrapper>
          </WrapperContainer>

          <ButtonsWrapper Container={FlexContainers.FlexRowEnd} width="100%" gap={8} mt={32}>
            <FormButton secondary onClick={onClose}>
              {isViewMode ? t(strCode.BACK_BTN) : t(strCode.CANCEL_BTN)}
            </FormButton>

            {!(isViewMode && !isActive) && !(isViewMode && !isVisible) && (
              <FormButton disabled={isSameData} onClick={onSubmit}>
                {consentsData ? t(strCode.SAVE_CHANGES_BTN) : t(strCode.SAVE_BTN)}
              </FormButton>
            )}
          </ButtonsWrapper>
        </>
      </WrapperContainer>
    </WrapperContainer>
  );
};

NewConsentForm.propTypes = {
  consentsData: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    position: PropTypes.number.isRequired,
    code: PropTypes.string.isRequired,
    isConsentVisible: PropTypes.bool.isRequired,

    consentFlags: PropTypes.arrayOf(PropTypes.string).isRequired,
    createdAtTs: PropTypes.number.isRequired,
    updatedAtTs: PropTypes.number.isRequired,
    groupName: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,

    texts: PropTypes.arrayOf(
      PropTypes.shape({
        languageCode: PropTypes.string.isRequired,
        content: PropTypes.string.isRequired,
        consentTitle: PropTypes.string
      })
    )
  }),
  consentPosition: PropTypes.number,
  onSaveConsent: PropTypes.func.isRequired,
  isViewMode: PropTypes.bool,
  viewOnlyLangs: PropTypes.arrayOf(PropTypes.string),
  isActive: PropTypes.bool,
  isToggleVisibilityDisabled: PropTypes.bool
};

export default NewConsentForm;
